// The platforms a far-end return plays (DECISIONS 2026-10-06 "far-end
// returns", "broadcaster directory"): their names, what a channel is on
// each, and the address of their own embedded player. Walls and the
// broadcaster directory share them; central checks channels the same way
// (central.checkChannel).

export type Platform = 'twitch' | 'youtube' | 'kick' | 'web'

export const platforms: { id: Platform; name: string; field: string; placeholder: string }[] = [
    { id: 'twitch', name: 'Twitch', field: 'channel', placeholder: 'e.g. blastpremier' },
    {
        id: 'youtube',
        name: 'YouTube',
        field: 'channel id (UC…) or video id',
        placeholder: 'e.g. UCdH8JPVyh2jsAwrWUcspUOw',
    },
    { id: 'kick', name: 'Kick', field: 'channel', placeholder: 'e.g. blast' },
    { id: 'web', name: 'Web page', field: 'link (https)', placeholder: 'https://…' },
]

export const isPlatform = (s: string): s is Platform => platforms.some((p) => p.id === s)
export const platformName = (p: Platform | undefined) => platforms.find((x) => x.id === p)?.name ?? ''

// the player's address, muted or with sound; a web page is the link itself
export function embedSrc(platform: Platform, channel: string, muted: boolean): string {
    switch (platform) {
        case 'twitch': {
            const q = new URLSearchParams({
                channel,
                parent: location.hostname,
                autoplay: 'true',
                muted: String(muted),
            })
            return `https://player.twitch.tv/?${q}`
        }
        case 'youtube': {
            const q = new URLSearchParams({ autoplay: '1', mute: muted ? '1' : '0', playsinline: '1' })
            // a channel's live stream (UC…), or one video
            return channel.startsWith('UC') && channel.length === 24
                ? `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(channel)}&${q}`
                : `https://www.youtube.com/embed/${encodeURIComponent(channel)}?${q}`
        }
        case 'kick':
            return `https://player.kick.com/${encodeURIComponent(channel)}?${new URLSearchParams({ autoplay: 'true', muted: String(muted) })}`
        case 'web':
            return channel
    }
}

// where a person watches it in a tab
export function watchURL(platform: Platform, channel: string): string {
    switch (platform) {
        case 'twitch':
            return `https://www.twitch.tv/${encodeURIComponent(channel)}`
        case 'youtube':
            return channel.startsWith('UC') && channel.length === 24
                ? `https://www.youtube.com/channel/${encodeURIComponent(channel)}/live`
                : `https://www.youtube.com/watch?v=${encodeURIComponent(channel)}`
        case 'kick':
            return `https://kick.com/${encodeURIComponent(channel)}`
        case 'web':
            return channel
    }
}

// frameAllow is what a far-end tile's frame may do: the platforms' players
// play, protected media too, and full screen; a web page (any link) only
// plays (MON-14: with fullscreen it could cover the MCR's TV with
// whatever it liked after a click inside it)
export const frameAllow = (p: Platform) => (p === 'web' ? 'autoplay' : 'autoplay; fullscreen; encrypted-media')

// YouTube's backup ingest for its main one (1+1 pairs, DECISIONS 2026-10-10
// "1+1: a destination's backup address"): RTMP(S) a. → b. with ?backup=1
// after the app, HLS a.upload → b.upload with copy=1. '' for anything else.
export function youtubeBackup(url: string): string {
    const rtmp = /^(rtmps?):\/\/a\.(rtmps?\.youtube\.com(?::\d+)?)\/live2\/(.+)$/.exec(url)
    if (rtmp) return `${rtmp[1]}://b.${rtmp[2]}/live2?backup=1/${rtmp[3]}`
    if (/^https:\/\/a\.upload\.youtube\.com\/http_upload_hls\?/.test(url) && /[?&]copy=0(&|$)/.test(url))
        return url.replace('https://a.upload.', 'https://b.upload.').replace(/([?&])copy=0(&|$)/, '$1copy=1$2')
    return ''
}

// Twitch takes one stream per stream key: a second connection replaces the
// first, and most channels have no backup ingest
export const isTwitchIngest = (url: string) =>
    /(^|\.)(twitch\.tv|contribute\.live-video\.net)(:\d+)?\//.test(url.replace(/^[a-z]+:\/\//, ''))
